/**
 * What the contact profile says, worked out from what the record carries.
 *
 * Everything here is a pure function of a `CrmContact` (and, for notes and
 * merges, the contact's activity trail) and a clock passed in by the caller.
 * Nothing is read from anywhere else and nothing is invented: an event the
 * record does not carry is not on the timeline, a deal without a date is
 * counted as a deal and placed in no window, and a figure with no data behind
 * it comes back null so the screen can say "Not on record" instead of 0.
 *
 * THE RULES BELOW ARE CHOSEN, NOT RATIFIED. Each constant is listed for
 * sign-off in the pull request that introduced it. They live here, in one
 * place, so changing a number changes the sentence the screen shows with it:
 * the screen reads `nextAction().rule`, it does not restate the rule.
 *
 * The one rule that is not ours is what counts as a routing identifier. That
 * is the platform's own (`contact_names.is_routing_address`), applied by the
 * server and handed over as `routingAddresses`; this file carries no pattern
 * for it.
 */
import { crmErrorDetail, crmErrorStatus, type CrmActivity, type CrmContact, type CrmDealHistoryRow } from '@/services/crm'
import type { RelationshipDefinitions, RelationshipRow } from '@/services/crmRelationships'
import { parseDateValue } from '@/utils/dates'
import { formatUsd } from '@/utils/money'
import { dealRowsFor, distinctCommunications, formatDate, type ListedCommunication } from './crmFields'

// ── Rules. UNRATIFIED: each value needs Edward's sign-off. ────────────────────

/** A quiet spell is flagged on the timeline once the last touch is this old. */
export const QUIET_GAP_DAYS = 14
/** The window "recent" means in the figures: deals and messages. */
export const RECENT_WINDOW_DAYS = 90
/** "The last twelve months", in days. */
export const YEAR_WINDOW_DAYS = 365
/** Next best action: the most recent deal must be at least this old. */
export const NEXT_ACTION_QUIET_DAYS = 30
/** Next best action: another deal must have landed within this many days
 *  before the most recent one, or there was no run to go quiet from. */
export const NEXT_ACTION_RUN_DAYS = 90
/** Next best action: how many such earlier deals the rule needs. */
export const NEXT_ACTION_MIN_EARLIER_DEALS = 1
/** The loan stage that means a deal funded, as the pipeline spells it. */
export const FUNDED_STAGE = 'Funded'
/** Months on the deals chart, current month last. */
export const CHART_MONTHS = 12

const DAY_MS = 86_400_000
/** A dated event further ahead than this is a clock or data error, not news. */
const FUTURE_TOLERANCE_MS = DAY_MS

// ── Dates ─────────────────────────────────────────────────────────────────────

const NAIVE_INSTANT_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/

/**
 * A recorded date or timestamp as a Date, or null when it names none.
 *
 * A date-only value is that calendar day in the reader's zone. A timestamp with
 * no zone designator is UTC: SQLite drops the zone on the way out, and every
 * naive timestamp on the platform is UTC (`utils/dates`). `new Date()` would
 * read it as the browser's local time, which put a contact's "last seen" hours
 * after the very message that set it.
 */
export function whenOf(value: string | null | undefined): Date | null {
  const text = String(value ?? '').trim()
  if (NAIVE_INSTANT_RE.test(text)) {
    const instant = new Date(`${text.replace(' ', 'T')}Z`)
    return Number.isNaN(instant.getTime()) ? null : instant
  }
  return parseDateValue(text || null)
}

/** "Sep 18, 2026" for a day on the record. */
export function formatDay(when: Date): string {
  return when.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/** Whole days from `then` to `now`; negative when `then` is ahead. */
export function daysBetween(then: Date, now: Date): number {
  return Math.floor((now.getTime() - then.getTime()) / DAY_MS)
}

function isPlausible(when: Date | null, now: Date): when is Date {
  return when !== null && when.getTime() - now.getTime() <= FUTURE_TOLERANCE_MS
}

/** "today", "1 day", "38 days", "5 months": a span the way a desk says it. */
export function spanLabel(days: number): string {
  if (days < 1) return 'today'
  if (days === 1) return '1 day'
  if (days < 120) return `${days} days`
  const months = Math.floor(days / 30)
  if (months < 24) return `${months} months`
  return `${Math.floor(days / 365)} years`
}

/** "38 days ago", "today". */
export function agoLabel(days: number): string {
  const span = spanLabel(days)
  return span === 'today' ? 'today' : `${span} ago`
}

// ── Addresses ─────────────────────────────────────────────────────────────────

export interface AlternateAddress {
  address: string
  /** The part after the @, lower-case. */
  domain: string
  /** Whether it sits on the same domain as the primary mailbox. */
  sameDomainAsPrimary: boolean
  /** Where the record says this address was seen, when it says: the merged
   *  contact it came with. The record keeps no first-seen date per address. */
  source: string | null
}

export interface AddressGroups {
  /** The mailbox to write to: the primary address unless it is a routing
   *  identifier, then the first real mailbox on the record. */
  primary: string | null
  /** Real mailboxes other than the primary. */
  alternates: AlternateAddress[]
  /** Routing identifiers, each once. Never a way to reach anybody. */
  routing: string[]
}

/** A snapshot of the survivor's own earlier value, kept by a merge. It says what
 *  the value was, not where it came from. */
const BEFORE_MERGE_RE = /before merge$/i

const sameText = (a: unknown, b: unknown) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase()

/**
 * Where an observation of this exact value was recorded, from the contact's
 * observed-values list. Matched on the value as well as the field: the list
 * holds every value seen under a field name, and the first one is not
 * necessarily the one on the card.
 */
export function observedSource(
  extra: CrmContact['enrichment']['extra'] | undefined,
  fields: readonly string[],
  value: string,
): string | null {
  const row = (extra ?? []).find(
    (r) => fields.includes(r.field) && sameText(r.value, value) && r.source && !BEFORE_MERGE_RE.test(r.source),
  )
  if (!row) return null
  return row.observedAt ? `${row.source} · ${formatDate(row.observedAt)}` : row.source
}

const domainOf = (address: string): string => address.split('@')[1]?.trim().toLowerCase() ?? ''

/**
 * The record's addresses, split into the ones a person can write to and the
 * transport names Exchange put in a header.
 *
 * The split is the server's (`routingAddresses`), not ours. Nothing is
 * dropped: every address on the record lands in exactly one of the three.
 */
/**
 * The contact's location as the screens print it. A broker record has no city
 * of its own and carries its firm's (`locationBasis: 'firm'`): printed bare it
 * read as where the person sits (a Chicago broker shown at his New York firm's
 * city), so it says whose it is.
 */
export function locationText(contact: Pick<CrmContact, 'location' | 'locationBasis'>): string | null {
  if (!contact.location) return null
  return contact.locationBasis === 'firm' ? `${contact.location} (firm's city)` : contact.location
}

export function addressGroups(
  contact: Pick<CrmContact, 'email' | 'emails' | 'routingAddresses'> & { enrichment?: Pick<CrmContact['enrichment'], 'extra'> },
): AddressGroups {
  const routingSet = new Set((contact.routingAddresses ?? []).map((a) => a.toLowerCase()))
  const seen = new Set<string>()
  const real: string[] = []
  const routing: string[] = []
  for (const raw of [contact.email, ...(contact.emails ?? [])]) {
    const address = (raw ?? '').trim()
    if (!address) continue
    const key = address.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    if (routingSet.has(key)) routing.push(address)
    else real.push(address)
  }
  // Identifiers the server knows of that no longer sit in `emails` (the dedup
  // pass took them out and kept them as provenance).
  for (const key of routingSet) {
    if (!seen.has(key)) routing.push(key)
  }
  const primary = real[0] ?? null
  const primaryDomain = primary ? domainOf(primary) : ''
  return {
    primary,
    alternates: real.slice(1).map((address) => ({
      address,
      domain: domainOf(address),
      sameDomainAsPrimary: domainOf(address) === primaryDomain && primaryDomain !== '',
      source: observedSource(contact.enrichment?.extra, ['email', 'alternateEmail', 'emails'], address),
    })),
    routing,
  }
}

// ── Deals ─────────────────────────────────────────────────────────────────────

export interface DealFact {
  dealId: string
  property: string | null
  amount: number | null
  stage: string | null
  assetType: string | null
  role: string | null
  outcome: string | null
  at: Date | null
  atIso: string | null
}

const dealFact = (row: CrmDealHistoryRow): DealFact => ({
  dealId: row.dealId,
  property: row.property,
  amount: row.amount ?? null,
  stage: row.stage,
  assetType: row.assetType ?? null,
  role: row.role ?? null,
  outcome: row.outcome,
  at: whenOf(row.date),
  atIso: row.date,
})

/** The contact's linked deals, one per deal id, newest first, undated last. */
export function dealFacts(contact: Pick<CrmContact, 'dealHistory' | 'linkedDeals'>): DealFact[] {
  return sortNewestFirst(dealRowsFor(contact).map(dealFact), (d) => d.at)
}

const isFunded = (deal: Pick<DealFact, 'stage'>) => (deal.stage ?? '').trim().toLowerCase() === FUNDED_STAGE.toLowerCase()

function sortNewestFirst<T>(items: T[], at: (item: T) => Date | null): T[] {
  return items
    .map((item, index) => ({ item, index, time: at(item)?.getTime() ?? null }))
    .sort((a, b) => {
      if (a.time === null && b.time === null) return a.index - b.index
      if (a.time === null) return 1
      if (b.time === null) return -1
      return b.time - a.time || a.index - b.index
    })
    .map((entry) => entry.item)
}

export interface MonthBucket {
  /** `YYYY-MM`. */
  key: string
  label: string
  year: number
  deals: number
  funded: number
}

/** The last `months` calendar months, current last, with the deals dated in each. */
export function dealsByMonth(
  deals: readonly DealFact[],
  now: Date,
  months: number = CHART_MONTHS,
): { buckets: MonthBucket[]; undated: number; earlier: number } {
  const buckets: MonthBucket[] = []
  for (let back = months - 1; back >= 0; back -= 1) {
    const first = new Date(now.getFullYear(), now.getMonth() - back, 1)
    buckets.push({
      key: `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, '0')}`,
      label: first.toLocaleDateString(undefined, { month: 'short' }),
      year: first.getFullYear(),
      deals: 0,
      funded: 0,
    })
  }
  const byKey = new Map(buckets.map((b) => [b.key, b]))
  let undated = 0
  let earlier = 0
  for (const deal of deals) {
    if (!deal.at) {
      undated += 1
      continue
    }
    const key = `${deal.at.getFullYear()}-${String(deal.at.getMonth() + 1).padStart(2, '0')}`
    const bucket = byKey.get(key)
    if (bucket) {
      bucket.deals += 1
      if (isFunded(deal)) bucket.funded += 1
    } else if (deal.at < new Date(now.getFullYear(), now.getMonth() - (months - 1), 1)) {
      earlier += 1
    }
  }
  return { buckets, undated, earlier }
}

export interface TrackRecord {
  deals: number
  /** Null when no linked deal has a stage on record: "none funded" and "no
   *  stage known" are different statements. */
  funded: number | null
  stagesKnown: number
  /** Null when no linked deal has an amount on record. */
  volume: number | null
  amountsKnown: number
  fundedVolume: number | null
  /** Asset types by count, largest first. */
  mix: { label: string; count: number }[]
  assetTypesKnown: number
}

export function trackRecord(deals: readonly DealFact[]): TrackRecord {
  const staged = deals.filter((d) => d.stage)
  const priced = deals.filter((d) => d.amount != null)
  const fundedDeals = staged.filter(isFunded)
  const fundedPriced = fundedDeals.filter((d) => d.amount != null)
  const counts = new Map<string, number>()
  for (const deal of deals) {
    const label = (deal.assetType ?? '').trim()
    if (label) counts.set(label, (counts.get(label) ?? 0) + 1)
  }
  const mix = [...counts].map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
  return {
    deals: deals.length,
    funded: staged.length ? fundedDeals.length : null,
    stagesKnown: staged.length,
    volume: priced.length ? priced.reduce((sum, d) => sum + (d.amount as number), 0) : null,
    amountsKnown: priced.length,
    fundedVolume: fundedPriced.length ? fundedPriced.reduce((sum, d) => sum + (d.amount as number), 0) : null,
    mix,
    assetTypesKnown: mix.reduce((sum, m) => sum + m.count, 0),
  }
}

// ── The timeline ──────────────────────────────────────────────────────────────

export type EventKind = 'email' | 'deal' | 'call' | 'meeting' | 'note'

export const EVENT_LABELS: Record<EventKind, string> = {
  email: 'Email',
  deal: 'Deal',
  call: 'Call',
  meeting: 'Meeting',
  note: 'Note',
}

export type TimelineFilter = 'all' | 'email' | 'deal' | 'people' | 'note'

export const TIMELINE_FILTERS: { key: TimelineFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'email', label: 'Emails' },
  { key: 'deal', label: 'Deals' },
  { key: 'people', label: 'Calls and meetings' },
  { key: 'note', label: 'Notes' },
]

export function matchesFilter(kind: EventKind, filter: TimelineFilter): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'people':
      return kind === 'call' || kind === 'meeting'
    default:
      return kind === filter
  }
}

export interface ProfileEvent {
  id: string
  kind: EventKind
  at: Date | null
  atIso: string | null
  title: string
  detail: string | null
  /** Right-hand label: a deal's stage, a note's author. */
  tag: string | null
  dealId: string | null
  /** The stored message, when the event is one. */
  ingestionId: string | null
}

function communicationKind(type: string): EventKind {
  if (/call/i.test(type)) return 'call'
  if (/meeting/i.test(type)) return 'meeting'
  if (/note/i.test(type)) return 'note'
  if (/^\s*(e-?mail|message)\b/i.test(type)) return 'email'
  // A logged entry of a type this screen has no name for is still something
  // somebody recorded; filing it under Notes beats dropping it.
  return 'note'
}

function emailDetail(comm: ListedCommunication): string | null {
  const parts = [
    comm.direction ?? null,
    comm.capacities.length ? `On the message as ${comm.capacities.join(', ')}` : null,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

function loggedDetail(comm: ListedCommunication): string | null {
  const body = comm.preview && comm.preview !== comm.subject ? comm.preview : null
  const parts = [body, comm.direction, comm.user ? `Logged by ${comm.user}` : null].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

const noteText = (entry: CrmActivity): string => {
  const text = entry.detail?.text
  return typeof text === 'string' && text.trim() ? text.trim() : entry.summary
}

/**
 * Everything on record that happened with this contact, newest first.
 *
 * Messages and hand-logged calls, meetings and notes come off the
 * communication log; deals off the deal history; notes added in the CRM off
 * the activity trail. Undated entries are kept and sorted last, never given a
 * date. Record maintenance (a merge, an edit, a lookup) is not a thing that
 * happened with the contact and is not here; the record history keeps it.
 */
export function buildEvents(contact: CrmContact, activity: readonly CrmActivity[] = []): ProfileEvent[] {
  const events: ProfileEvent[] = []

  for (const comm of distinctCommunications(contact.communications ?? [])) {
    const kind = communicationKind(comm.type)
    events.push({
      id: `comm:${comm.id}`,
      kind,
      at: whenOf(comm.timestamp),
      atIso: comm.timestamp,
      title: comm.subject ?? (kind === 'email' ? 'Email, no subject recorded' : comm.type),
      detail: kind === 'email' ? emailDetail(comm) : loggedDetail(comm),
      tag: null,
      dealId: comm.dealId ?? null,
      ingestionId: comm.ingestionId ?? null,
    })
  }

  for (const deal of dealFacts(contact)) {
    const facts = [
      deal.amount != null ? formatUsd(deal.amount) : null,
      deal.assetType,
      deal.role ? `as ${deal.role}` : null,
    ].filter(Boolean)
    events.push({
      id: `deal:${deal.dealId}`,
      kind: 'deal',
      at: deal.at,
      atIso: deal.atIso,
      title: deal.property ?? `Deal ${deal.dealId}`,
      detail: facts.length ? facts.join(' · ') : null,
      tag: deal.stage ?? deal.outcome,
      dealId: deal.dealId,
      ingestionId: null,
    })
  }

  for (const entry of activity) {
    if (entry.kind !== 'note') continue
    events.push({
      id: `note:${entry.id}`,
      kind: 'note',
      at: whenOf(entry.createdAt),
      atIso: entry.createdAt,
      title: noteText(entry),
      detail: entry.actor ? `Added by ${entry.actor}` : null,
      tag: null,
      dealId: null,
      ingestionId: null,
    })
  }

  return sortNewestFirst(events, (e) => e.at)
}

// ── Relationship facts ────────────────────────────────────────────────────────

export interface RelationshipFacts {
  lastTouchAt: Date | null
  /** What the last touch was; 'sighting' is the record's own last-seen stamp
   *  when no listed event is as recent. */
  lastTouchKind: EventKind | 'sighting' | null
  daysSinceLastTouch: number | null
  dealsLast12Months: number
  dealsLast90Days: number
  undatedDeals: number
  /** Null when no deal in the last twelve months has an amount. */
  volumeLast12Months: number | null
  volumeAmountsKnown: number
  messagesTotal: number
  messagesLast90Days: number
  firstSeen: Date | null
}

/**
 * The figures a desk reads a relationship by, each counted from dated records.
 *
 * A touch is a message, a logged call or meeting, a dated deal, or the
 * record's own last-seen stamp. A note is the desk's annotation, not contact
 * with the person, so it is not a touch. Events dated more than a day ahead
 * are ignored here (and stay on the timeline).
 */
export function relationshipFacts(contact: CrmContact, events: readonly ProfileEvent[], now: Date): RelationshipFacts {
  const touches = events.filter((e) => e.kind !== 'note' && isPlausible(e.at, now))
  let last: { at: Date; kind: EventKind | 'sighting' } | null = null
  for (const touch of touches) {
    if (touch.at && (!last || touch.at > last.at)) last = { at: touch.at, kind: touch.kind }
  }
  const seen = whenOf(contact.lastContact)
  if (isPlausible(seen, now) && (!last || seen > last.at)) last = { at: seen, kind: 'sighting' }

  const deals = dealFacts(contact)
  const dated = deals.filter((d) => isPlausible(d.at, now))
  const inLast = (days: number) => dated.filter((d) => (d.at as Date) >= new Date(now.getTime() - days * DAY_MS))
  const year = inLast(YEAR_WINDOW_DAYS)
  const priced = year.filter((d) => d.amount != null)

  const messages = events.filter((e) => e.kind === 'email')
  const recentMessages = messages.filter(
    (e) => isPlausible(e.at, now) && (e.at as Date) >= new Date(now.getTime() - RECENT_WINDOW_DAYS * DAY_MS),
  )

  return {
    lastTouchAt: last?.at ?? null,
    lastTouchKind: last?.kind ?? null,
    daysSinceLastTouch: last ? Math.max(0, daysBetween(last.at, now)) : null,
    dealsLast12Months: year.length,
    dealsLast90Days: inLast(RECENT_WINDOW_DAYS).length,
    undatedDeals: deals.filter((d) => !d.at).length,
    volumeLast12Months: priced.length ? priced.reduce((sum, d) => sum + (d.amount as number), 0) : null,
    volumeAmountsKnown: priced.length,
    messagesTotal: messages.length,
    messagesLast90Days: recentMessages.length,
    firstSeen: whenOf(contact.firstSeen),
  }
}

/** What the last touch was, in a phrase for "Last touch was …". */
export function touchPhrase(kind: RelationshipFacts['lastTouchKind']): string {
  switch (kind) {
    case 'email':
      return 'an email'
    case 'deal':
      return 'a deal'
    case 'call':
      return 'a call'
    case 'meeting':
      return 'a meeting'
    case 'sighting':
      return 'a sighting on record'
    default:
      return 'something'
  }
}

/** The quiet-spell marker for the timeline, or null when there is no gap to flag. */
export function quietGap(facts: RelationshipFacts): { days: number; since: Date; phrase: string } | null {
  if (facts.lastTouchAt === null || facts.daysSinceLastTouch === null) return null
  if (facts.daysSinceLastTouch < QUIET_GAP_DAYS) return null
  return { days: facts.daysSinceLastTouch, since: facts.lastTouchAt, phrase: touchPhrase(facts.lastTouchKind) }
}

// ── Next best action ──────────────────────────────────────────────────────────

export interface NextAction {
  title: string
  body: string
  /** The rule that put this here, in words, so the card never asks to be trusted. */
  rule: string
  /** The deal to open. */
  dealId: string
  dealLabel: string
}

/**
 * The one suggestion the profile makes, or null.
 *
 * Rule: the contact's most recent dated deal is at least
 * NEXT_ACTION_QUIET_DAYS old, and at least NEXT_ACTION_MIN_EARLIER_DEALS other
 * deal fell within NEXT_ACTION_RUN_DAYS before it: a source that was sending
 * and stopped. Nothing is drafted and nothing is sent; the actions it offers
 * open a deal and the contact's own address.
 */
export function nextAction(deals: readonly DealFact[], now: Date): NextAction | null {
  const dated = deals.filter((d) => isPlausible(d.at, now)).sort((a, b) => (b.at as Date).getTime() - (a.at as Date).getTime())
  const latest = dated[0]
  if (!latest?.at) return null
  const quietDays = daysBetween(latest.at, now)
  if (quietDays < NEXT_ACTION_QUIET_DAYS) return null
  const earlier = dated.slice(1).filter((d) => daysBetween(d.at as Date, latest.at as Date) <= NEXT_ACTION_RUN_DAYS)
  if (earlier.length < NEXT_ACTION_MIN_EARLIER_DEALS) return null
  const label = latest.property ?? latest.dealId
  return {
    title: `No new deal in ${spanLabel(quietDays)}`,
    body:
      `The last deal was ${label}, ${formatDay(latest.at)}. ` +
      `${earlier.length === 1 ? 'One other deal' : `${earlier.length} other deals`} came in the ${NEXT_ACTION_RUN_DAYS} days before it.`,
    rule:
      `Shown when the most recent deal is ${NEXT_ACTION_QUIET_DAYS} or more days old and ` +
      `${NEXT_ACTION_MIN_EARLIER_DEALS === 1 ? 'another deal' : `${NEXT_ACTION_MIN_EARLIER_DEALS} other deals`} ` +
      `fell within ${NEXT_ACTION_RUN_DAYS} days before it.`,
    dealId: latest.dealId,
    dealLabel: label,
  }
}

/**
 * The next action when the relationships board has staged this contact: its
 * own one-line follow-up for a Cooling contact, with its own rule beside it,
 * so the profile and the board's "Going quiet" list cannot disagree. The deal
 * to open is the contact's most recent dated one.
 */
export function boardAction(
  row: Pick<RelationshipRow, 'stage' | 'suggestion'>,
  definitions: Pick<RelationshipDefinitions, 'stages'>,
  deals: readonly DealFact[],
  who: string,
): NextAction | null {
  if (row.stage !== 'cooling' || !row.suggestion) return null
  const latest = deals.find((d) => d.at)
  if (!latest) return null
  const rule = definitions.stages.find((s) => s.key === 'cooling')?.rule
  return {
    title: `Check in with ${who}`,
    body: row.suggestion,
    rule: rule ? `Shown for a contact the board marks Cooling: ${rule}` : 'Shown for a contact the board marks Cooling.',
    dealId: latest.dealId,
    dealLabel: latest.property ?? latest.dealId,
  }
}

// ── Merges ────────────────────────────────────────────────────────────────────

export interface MergedRecord {
  id: string
  name: string
  at: string | null
  automatic: boolean
  actor: string | null
}

/**
 * Records folded into this one, from the contact's own activity trail, less
 * the merges that have since been undone (an `unmerge` row names the merge it
 * undoes). Newest first.
 */
export function mergedFrom(contactId: string, activity: readonly CrmActivity[]): MergedRecord[] {
  const undone = new Set(
    activity.filter((a) => a.kind === 'unmerge').map((a) => String(a.detail?.undoes ?? '')).filter(Boolean),
  )
  return activity
    .filter((a) => (a.kind === 'merge' || a.kind === 'dedupe') && a.detail?.action !== 'split')
    .filter((a) => a.detail?.targetId === contactId && a.detail?.sourceId && !undone.has(a.id))
    .map((a) => ({
      id: String(a.detail.sourceId),
      name: typeof a.detail.sourceName === 'string' && a.detail.sourceName ? a.detail.sourceName : String(a.detail.sourceId),
      at: a.createdAt,
      automatic: a.kind === 'dedupe',
      actor: a.actor,
    }))
}

// ── What we know ──────────────────────────────────────────────────────────────

export interface KnownFact {
  key: string
  label: string
  text: string
  /** `stated` facts are the record's own and a lookup's findings; `observed`
   *  ones are values seen under another record or field and kept with their source. */
  group: 'stated' | 'observed'
  /** Who or what stated it; null when nothing on record says. */
  source: string | null
  observedAt: string | null
  /** A person set it by hand. */
  pinned: boolean
}

export interface Observation {
  field: string
  source: string | null
  observedAt: string | null
  pinned?: boolean
}

/** Fields the Reach card already draws; "also observed" does not repeat them. */
const SHOWN_ELSEWHERE = new Set([
  'directPhone',
  'mobilePhone',
  'officePhone',
  'address',
  'linkedIn',
  'linkedin',
  'website',
  'companyDomain',
  'company_domain',
])

const ENRICHMENT_LABELS: Record<string, string> = {
  yearsExperience: 'Experience',
  dealVolume: 'Deal volume',
  education: 'Education',
  licenses: 'Licenses',
  previousCompanies: 'Previous firms',
  specialties: 'Specialties',
}

function valueText(value: unknown): string {
  if (Array.isArray(value)) return value.map((v) => valueText(v)).join(', ')
  if (value !== null && typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * The facts the platform holds about this contact beyond how to reach them,
 * each with where it came from and when. No confidence is shown because none
 * is stored.
 *
 * Enrichment values keep no source of their own on the contact; their source
 * and date are the latest observation of the same field, when the trail has
 * been read. A fact whose source cannot be found says so (`source: null`).
 */
export function knownFacts(contact: CrmContact, observations: readonly Observation[] | null): KnownFact[] {
  const e = contact.enrichment
  const latest = (field: string): Observation | undefined => observations?.find((o) => o.field === field)
  const facts: KnownFact[] = []
  const add = (
    key: string,
    label: string,
    text: string,
    observed?: { source: string | null; observedAt: string | null; pinned?: boolean },
    group: KnownFact['group'] = 'stated',
  ) => {
    facts.push({
      key,
      label,
      text,
      group,
      source: observed?.source ?? null,
      observedAt: observed?.observedAt ?? null,
      pinned: Boolean(observed?.pinned),
    })
  }
  const enriched = (field: keyof typeof ENRICHMENT_LABELS, text: string) => {
    const seen = latest(field)
    add(field, ENRICHMENT_LABELS[field], text, seen)
  }

  if (e?.yearsExperience != null) enriched('yearsExperience', `${e.yearsExperience} years of experience`)
  if (e?.dealVolume) enriched('dealVolume', e.dealVolume)
  if (e?.education) enriched('education', e.education)
  if ((e?.licenses ?? []).length) enriched('licenses', e.licenses.join(', '))
  if ((e?.previousCompanies ?? []).length) enriched('previousCompanies', e.previousCompanies.join(' > '))
  if ((e?.specialties ?? []).length) enriched('specialties', e.specialties.join(', '))

  if ((contact.markets ?? []).length) {
    add('markets', 'Markets', contact.markets!.join(', '), { source: 'Entered on the record', observedAt: null })
  }
  if ((contact.assetClasses ?? []).length) {
    add('assetClasses', 'Asset classes', contact.assetClasses!.join(', '), { source: 'Entered on the record', observedAt: null })
  }
  if (contact.roleEvidence) {
    add('roleEvidence', `Why filed as ${contact.companyType}`, contact.roleEvidence, { source: 'Role rule at capture', observedAt: null })
  }

  // Values the card already states, and routing ids, which only ever appear
  // behind the routing group: an observation of either adds nothing here.
  const stated = new Set(
    [
      contact.displayName, contact.firstName, contact.lastName, contact.company, contact.title, contact.address,
      contact.email, ...(contact.emails ?? []), contact.phone, contact.companyDomain,
      contact.phones?.primary, contact.phones?.direct, contact.phones?.mobile, contact.phones?.office,
    ]
      .filter((v): v is string => Boolean(v))
      .map((v) => v.trim().toLowerCase()),
  )
  const routing = new Set((contact.routingAddresses ?? []).map((a) => a.toLowerCase()))
  for (const [index, row] of (e?.extra ?? []).entries()) {
    if (SHOWN_ELSEWHERE.has(row.field)) continue
    const text = valueText(row.value).trim()
    if (!text || stated.has(text.toLowerCase()) || routing.has(text.toLowerCase())) continue
    add(
      `extra:${row.field}:${index}`,
      labelFor(row.field),
      text,
      { source: row.source, observedAt: row.observedAt },
      'observed',
    )
  }
  return facts
}

function labelFor(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase())
}

// ── Reach provenance ──────────────────────────────────────────────────────────

const FIELD_COLUMNS: Record<string, string[]> = {
  email: ['email'],
  phone: ['phone', 'primary'],
  directPhone: ['direct_phone', 'directPhone', 'direct'],
  mobilePhone: ['mobile_phone', 'mobilePhone', 'mobile'],
  officePhone: ['office_phone', 'officePhone', 'office'],
  address: ['address'],
}

/**
 * Where the value on the card was stated, or null when nothing on record says.
 *
 * A hand edit wins; then the capture engine's stamp for the column (which always
 * describes the value the column holds now); then an observation of this exact
 * value. An observation of a different value under the same field name (the
 * survivor's old one, kept by a merge) is not the source of what is shown.
 */
export function sourceLine(contact: CrmContact, field: string, shown: string | null | undefined): string | null {
  const names = FIELD_COLUMNS[field] ?? [field]
  if ((contact.editedFields ?? []).some((name) => names.includes(name))) {
    return `Edited by ${contact.editedBy ?? 'a person'}`
  }
  if ((contact.enrichment?.pinnedFields ?? []).some((name) => names.includes(name))) return 'Set by hand'
  for (const name of names) {
    const entry = contact.fieldSources?.[name]
    if (entry?.source) return entry.observedAt ? `${entry.source} · ${formatDate(entry.observedAt)}` : entry.source
  }
  return shown ? observedSource(contact.enrichment?.extra, [field], shown) : null
}

/** Whether the profile scores this contact at all: the tenant's own people are
 *  colleagues, not relationships. */
export function isScored(contact: Pick<CrmContact, 'isInternal' | 'companyType'>): boolean {
  return !contact.isInternal && contact.companyType !== 'Internal'
}

/** The sentence for a read the reader's role cannot make, else the server's own. */
export function readError(error: unknown, fallback: string): string {
  return crmErrorStatus(error) === 403
    ? 'Your role does not include access to this part of the CRM.'
    : crmErrorDetail(error, fallback)
}

/** "1 deal", "4 deals". */
export function count(n: number, singular: string, plural = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : plural}`
}
